import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from 'services/authService';
import FormErrorBanner from 'components/errors/FormErrorBanner';
import InlineFieldError from 'components/errors/InlineFieldError';
import Icon from 'components/AppIcon';
import Logo from 'components/Logo';
import useSEO from 'hooks/useSEO';

export default function ForgotPasswordPage() {
  useSEO({ title: 'Forgot Password', noindex: true });
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [formError, setFormError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [isNetworkError, setIsNetworkError] = useState(false);

  const validate = () => {
    if (!email?.trim()) { setEmailError('Email address is required.'); return false; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(email)) { setEmailError('Please enter a valid email address.'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setFormError(null);
    setIsNetworkError(false);
    if (!validate()) return;
    setIsLoading(true);
    try {
      await forgotPassword(email);
      setIsSuccess(true);
    } catch (err) {
      const isNetwork = !err?.response && err?.request;
      if (isNetwork || err?.code === 'ECONNABORTED') {
        setIsNetworkError(true);
        setFormError('Network error. Check your connection and try again.');
      } else {
        setFormError(err?.response?.data?.message || 'Failed to send reset link. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="w-full" style={{ maxWidth: '420px' }}>
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="mb-4 inline-block">
            <Logo size={56} />
          </div>
          <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}>Safe Dividend Tracker</h1>
          <p className="text-sm" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}>Reset your password</p>
        </div>

        <div className="rounded-xl border p-6 md:p-8" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: '0 4px 24px -4px rgba(0,0,0,0.08)' }}>
          {isSuccess ? (
            <div className="text-center py-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4" style={{ backgroundColor: 'rgba(34,197,94,0.15)', border: '1px solid rgba(34,197,94,0.3)' }}>
                <Icon name="CheckCircle2" size={32} color="#22C55E" />
              </div>
              <h2 className="text-xl font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}>Check your inbox</h2>
              <p className="text-sm mb-6" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)', maxWidth: '100%' }}>
                Reset link sent to <span className="font-medium" style={{ color: 'var(--color-foreground)' }}>{email}</span>. Check your inbox and follow the instructions.
              </p>
              <Link
                to="/login"
                className="inline-flex items-center gap-2 text-sm font-medium transition-opacity hover:opacity-80"
                style={{ fontFamily: 'var(--font-caption)', color: '#2E7D32' }}
              >
                <Icon name="ArrowLeft" size={16} color="#2E7D32" />
                Back to Login
              </Link>
            </div>
          ) : (
            <>
              <h2 className="text-lg font-semibold mb-2" style={{ fontFamily: 'var(--font-heading)', color: 'var(--color-foreground)' }}>Forgot your password?</h2>
              <p className="text-sm mb-5" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)', maxWidth: '100%' }}>
                Enter your email address and we'll send you a link to reset your password.
              </p>

              <FormErrorBanner error={formError} onDismiss={() => { setFormError(null); setIsNetworkError(false); }} />

              {isNetworkError && (
                <div className="flex items-center justify-between px-3 py-2 rounded-lg mb-4 text-sm" style={{ backgroundColor: 'rgba(249,115,22,0.1)', border: '1px solid rgba(249,115,22,0.25)', color: '#F97316', fontFamily: 'var(--font-caption)' }}>
                  <span>Connection failed.</span>
                  <button onClick={handleSubmit} className="underline font-medium ml-2" style={{ color: '#F97316' }}>Retry</button>
                </div>
              )}

              <form onSubmit={handleSubmit} noValidate>
                <div className="mb-5">
                  <label className="block text-sm font-medium mb-1.5" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-foreground)' }}>Email Address</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e?.target?.value); setEmailError(''); }}
                    placeholder="you@example.com"
                    autoComplete="email"
                    className="input-base"
                    style={{ backgroundColor: 'var(--color-background)', borderColor: emailError ? '#EF4444' : 'var(--color-border)', color: 'var(--color-foreground)', height: '48px' }}
                  />
                  <InlineFieldError message={emailError} />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex items-center justify-center gap-2 rounded-lg font-medium transition-all hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{ height: '48px', backgroundColor: '#2E7D32', color: '#FFFFFF', fontFamily: 'var(--font-caption)', fontSize: '1rem' }}
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 rounded-full border-2 border-white border-t-transparent animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Icon name="Mail" size={18} color="#FFFFFF" />
                      Send Reset Link
                    </>
                  )}
                </button>
              </form>

              <div className="text-center mt-5">
                <Link to="/login" className="inline-flex items-center gap-1.5 text-sm transition-opacity hover:opacity-80" style={{ fontFamily: 'var(--font-caption)', color: 'var(--color-muted-foreground)' }}>
                  <Icon name="ArrowLeft" size={14} color="#94A3B8" />
                  Back to Login
                </Link>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
